import { useMemo, useState } from 'react';

import { DateTime } from 'luxon';
import { PurchasesSearch, useListCausesPurchased } from 'store/Api';
import { ActionIcon, ButtonMantine, ComboboxItem, Group, Modal, Select, Stack } from 'ui';
import { useDisclosure } from 'ui/Hooks';
import { IconFilter } from 'ui/Icons';

import { useUserPurchasesSearchContext } from '../PurchaseSearchContext';

const PurchasesFilterModalContent = ({ onClose }: { onClose: () => void }) => {
  const ctx = useUserPurchasesSearchContext();
  const { data } = useListCausesPurchased();

  const [categorySearch, setCategorySearch] = useState<PurchasesSearch['searches']>();

  const causeSelectData = useMemo(() => {
    const selectData = data?.causes
      .filter((cause) => cause.name)
      .map((cause) => {
        return {
          value: cause.uuid,
          label: cause.name,
        };
      });
    selectData?.unshift({ value: '', label: 'All Causes' });
    return selectData as ComboboxItem[];
  }, [data?.causes]);

  const yearSelectData = useMemo(() => {
    const currentYear = DateTime.now().year;
    const selectData = Array.from({ length: 3 }, (value, index) => currentYear - index).map(
      (year) => {
        return {
          value: String(year),
          label: String(year),
        };
      }
    );
    selectData?.unshift({ value: '', label: 'All' });
    return selectData as ComboboxItem[];
  }, []);

  const handleApply = () => {
    ctx.setUserPurchasesSearchObject({
      searches: { ...ctx.UserPurchasesSearchObject?.searches, ...categorySearch },
    });
    onClose();
  };

  // waiting for causeSelectData to get all data so select's default value can be set properly
  if (!data || !causeSelectData || !yearSelectData) return null;

  return (
    <Stack gap="lg">
      <Group grow>
        <Select
          leftSection="Year"
          leftSectionWidth={48}
          data={yearSelectData}
          defaultValue={DateTime.now().year.toString()}
          onChange={(value) => {
            setCategorySearch((prev) => ({
              ...prev,
              'carts.purchased': { value: value ?? '' },
            }));
          }}
        />
        <Select
          leftSection="Cause"
          leftSectionWidth={64}
          data={causeSelectData}
          defaultValue={''}
          onChange={(value) => {
            setCategorySearch((prev) => ({
              ...prev,
              'store_items.cause_id': { value: value ?? '' },
            }));
          }}
        />
      </Group>
      {/* To be implemented once all purchase types are accounted for here */}
      {/* <Checkbox.Group label="Select Purchases Type">
        <Stack>
          <Checkbox label="Campaign Donations" value="campaign_donations" />
          <Checkbox label="Direct Donations" value="direct_donations" />
          <Checkbox label="Store Purchases" value="purchase" />
        </Stack>
      </Checkbox.Group> */}
      <ButtonMantine fullWidth onClick={handleApply}>
        Apply Filters
      </ButtonMantine>
    </Stack>
  );
};

const PurchasesFilterModal = ({ isPurchasesEmpty }: { isPurchasesEmpty: boolean }) => {
  const [opened, handlers] = useDisclosure();

  return (
    <>
      <ActionIcon
        disabled={isPurchasesEmpty}
        variant="transparent"
        c="gray.8"
        onClick={() => handlers.open()}
      >
        <IconFilter />
      </ActionIcon>
      <Modal keepMounted opened={opened} onClose={() => handlers.close()} title="Filter Purchases">
        <PurchasesFilterModalContent onClose={() => handlers.close()} />
      </Modal>
    </>
  );
};

export { PurchasesFilterModal };
